// src/landing/LandingV2.jsx
// ─── Landing VehicleIQ V2 (§121-124) ────────────────────────────────────────
// Reconstruite à partir d'un shell vierge, hors du monolithe App.jsx. Aucun
// composant, style ou choix graphique n'est hérité de l'ancienne landing :
// seuls des SERVICES le sont — l'endpoint de lead, la navigation, les modales
// juridiques, l'authentification — passés en props par App.jsx.
//
// Un seul état de thème (§3), posé en data-theme sur la racine : tout ce qui
// est dessous change ensemble. Clair par défaut (§2), la préférence est
// mémorisée séparément de celle de l'espace user.

import { useState, useEffect } from "react";
import DemoLeadForm from "./DemoLeadForm";
import { publishedArticles, ARTICLES, categoryLabel } from "./blog-catalog";
import { ResourcesHub, ArticlePage } from "./ResourcesHub";
import "./landing.css";

const WA_NUMBER = (typeof process !== "undefined" && process.env && (process.env.REACT_APP_ADMIN_WHATSAPP || process.env.VITE_ADMIN_WHATSAPP)) || "";
const WA_TEXT = "Bonjour, je souhaite en savoir plus sur VehicleIQ et VIQ.AI.";

// ── Structured data (§73-75) ────────────────────────────────────────────────
function JsonLd({ data }) {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />;
}
const ORG = {
  "@context": "https://schema.org", "@type": "Organization",
  name: "VehicleIQ", url: "https://vehicleiq.fr", logo: "https://vehicleiq.fr/logo.svg",
  description: "Intelligence artificielle métier des professionnels du véhicule d'occasion.",
  address: { "@type": "PostalAddress", addressLocality: "Saint-Quentin", addressCountry: "FR" },
  sameAs: [],
};
const APP = {
  "@context": "https://schema.org", "@type": "SoftwareApplication",
  name: "VIQ.AI par VehicleIQ", applicationCategory: "BusinessApplication", operatingSystem: "Web",
  description: "Assistant IA pour professionnels du véhicule d'occasion : identification, historique, marché, marge, dossiers.",
  offers: { "@type": "Offer", price: "0", priceCurrency: "EUR", description: "7 jours d'essai gratuit, sans engagement" },
  provider: { "@type": "Organization", name: "VehicleIQ" },
};

// ── Navbar (§5-9) ───────────────────────────────────────────────────────────
function PublicNavbar({ theme, onTheme, nav, onLogin, onTry }) {
  const [open, setOpen] = useState(false);
  const links = [["Produit", "product"], ["VIQ.AI", "viqai"], ["Tarification", "pricing"], ["Ressources", "resources"], ["API", "api"]];
  return (
    <header className="lv2-nav">
      <div className="lv2-wrap lv2-nav-inner">
        <button className="lv2-brand" onClick={() => nav("/")}>Vehicle<span>IQ</span></button>
        <nav className="lv2-nav-links" aria-label="Navigation principale">
          {links.map(([l, k]) => <button key={k} onClick={() => nav(k)}>{l}</button>)}
        </nav>
        <div className="lv2-nav-right">
          <button className="lv2-btn secondary sm" onClick={onLogin}>Connexion</button>
          <button className="lv2-btn primary sm" onClick={onTry}>Essayer VIQ.AI</button>
          <button className="lv2-theme" onClick={onTheme} aria-label={theme === "dark" ? "Passer en clair" : "Passer en sombre"}>
            {theme === "dark" ? "☀" : "☾"}
          </button>
          <button className="lv2-theme lv2-burger" onClick={() => setOpen(true)} aria-label="Menu">☰</button>
        </div>
      </div>
      {open && (
        <div className="lv2-drawer" role="dialog" aria-label="Menu">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span className="lv2-brand">Vehicle<span>IQ</span></span>
            <button className="lv2-theme" onClick={() => setOpen(false)} aria-label="Fermer">✕</button>
          </div>
          {links.map(([l, k]) => <button key={k} className="link" onClick={() => { setOpen(false); nav(k); }}>{l}</button>)}
          <button className="link" onClick={() => { setOpen(false); onLogin(); }}>Connexion</button>
          <button className="lv2-btn primary lg" style={{ marginTop: "1rem" }} onClick={() => { setOpen(false); onTry(); }}>Essayer VIQ.AI</button>
        </div>
      )}
    </header>
  );
}

// ── Silhouette (SVG, aucune marque) ────────────────────────────────────────
function CarSilhouette({ className }) {
  return (
    <svg className={className} viewBox="0 0 900 300" aria-hidden="true" focusable="false">
      <path d="M30 230 L100 230 C126 230 138 190 180 175 C252 150 320 140 396 138 C472 136 540 145 608 160 C676 175 744 197 802 205 L860 213"
        fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M214 170 C266 112 334 90 418 88 C502 86 578 108 630 160" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="272" cy="230" r="42" fill="none" stroke="currentColor" strokeWidth="2.5" />
      <circle cx="716" cy="230" r="42" fill="none" stroke="currentColor" strokeWidth="2.5" />
    </svg>
  );
}

// ── Hero (§15-19, §62-64) ───────────────────────────────────────────────────
function HeroV2({ apiUrl, onTry, onPrivacy, nav }) {
  return (
    <section className="lv2-hero" id="product">
      <CarSilhouette className="lv2-silhouette" />
      <div className="lv2-wrap lv2-hero-grid">
        <div>
          <div className="lv2-eyebrow">VehicleIQ · Intelligence automobile professionnelle</div>
          <h1 className="lv2-h1">L'<em>intelligence artificielle</em> métier du véhicule d'occasion.</h1>
          <p className="lv2-hero-sub">Analysez vos véhicules, contrôlez leur historique, comparez le marché et pilotez vos dossiers depuis une seule intelligence métier.</p>
          <p className="lv2-hero-tag">Demandez. VIQ.AI analyse et agit.</p>
          <div className="lv2-hero-cta">
            <div>
              <button className="lv2-btn primary lg" onClick={onTry}>Essayer VIQ.AI</button>
              <span className="lv2-hero-cta-note">7 jours gratuits · sans engagement</span>
            </div>
            <button className="lv2-btn secondary lg" onClick={() => nav("viqai")}>Voir VIQ.AI en action</button>
          </div>
          {/* §62-63 — preuves techniques, pas de chiffres de vanité */}
          <div className="lv2-proof">
            <span>API SIV</span><span>HistoVec</span><span>CarVertical</span><span>Vision IA</span><span>Données marché</span>
          </div>
        </div>
        <DemoLeadForm apiUrl={apiUrl} onPrivacy={onPrivacy} onDiscover={() => nav("product")} variant="hero" />
      </div>
    </section>
  );
}

// ── Moteurs IA (§40-44) ─────────────────────────────────────────────────────
const PROVIDERS = [
  { id: "anthropic", name: "Claude", file: "claude", desc: "Analyse technique & vision" },
  { id: "openai", name: "ChatGPT", file: "openai", desc: "Texte & recherche" },
  { id: "gemini", name: "Gemini", file: "gemini", desc: "Photo & vidéo", soon: true },
  { id: "mistral", name: "Mistral", file: "mistral", desc: "Documents & automatisation", soon: true },
];
function BrandLogo({ file, name }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <span className="lv2-provider-name">{name}</span>;
  return <img src={`/brands/${file}.svg`} alt={name} className="lv2-provider-logo" loading="lazy" onError={() => setFailed(true)} />;
}
function AIProviders({ live }) {
  return (
    <section className="lv2-section" id="viqai">
      <div className="lv2-wrap lv2-center">
        <div className="lv2-eyebrow">Les moteurs</div>
        <h2 className="lv2-h2">VIQ.AI orchestre les meilleurs moteurs d'intelligence artificielle.</h2>
        <p className="lv2-lead">Plusieurs moteurs, choisis selon la tâche. Une seule intelligence métier — VehicleIQ.</p>
        <div className="lv2-providers">
          {PROVIDERS.map(p => {
            const l = (live || []).find(x => x.id === p.id);
            const soon = l ? !l.available : p.soon;
            return (
              <div key={p.id} className={`lv2-provider${soon ? " soon" : ""}`}>
                <div className="lv2-provider-head"><BrandLogo file={p.file} name={p.name} />{soon && <span className="lv2-badge">Bientôt</span>}</div>
                <div className="lv2-provider-desc">{p.desc}</div>
              </div>
            );
          })}
        </div>
        <p className="lv2-disclaimer">Les marques citées appartiennent à leurs propriétaires respectifs. VehicleIQ orchestre ces moteurs via ses propres workflows métier.</p>
      </div>
    </section>
  );
}

// ── Capacités (§45-52) ──────────────────────────────────────────────────────
const CAPS = [
  ["Identifier", ["Plaque", "VIN", "Photos", "Documents"]],
  ["Contrôler", ["HistoVec", "CarVertical", "Historique", "Risques"]],
  ["Analyser", ["Vision", "État", "Remise en état", "Cohérence véhicule"]],
  ["Comparer", ["Leboncoin", "La Centrale", "Marché", "Cote"]],
  ["Décider", ["Prix d'achat", "Marge", "Risques", "Opportunité"]],
  ["Agir", ["Dossier", "Annonce", "Lead", "Agenda", "Vente"]],
];
function VIQCapabilities() {
  return (
    <section className="lv2-section alt">
      <div className="lv2-wrap">
        <div className="lv2-eyebrow">IA pour professionnels du véhicule d'occasion</div>
        <h2 className="lv2-h2">Une conversation. <em>Tout votre workflow VO.</em></h2>
        <div className="lv2-caps">
          {CAPS.map(([t, items]) => (
            <div key={t} className="lv2-cap">
              <div className="lv2-cap-title">{t}</div>
              <ul className="lv2-cap-list">{items.map(i => <li key={i}>{i}</li>)}</ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Démo conversationnelle statique (§53-54) ────────────────────────────────
function ConversationDemo() {
  return (
    <section className="lv2-section">
      <div className="lv2-wrap lv2-center">
        <div className="lv2-eyebrow">Analyse et estimation des véhicules d'occasion</div>
        <h2 className="lv2-h2">Vous demandez. <em>VIQ.AI enchaîne.</em></h2>
        <div className="lv2-demo" aria-label="Exemple de conversation avec VIQ.AI">
          <div className="lv2-demo-user">Analyse cette Audi A3 à 19 500 €.</div>
          <div className="lv2-demo-ai">
            <div className="ok">Véhicule identifié — Audi A3 Sportback 35 TDI, 2021</div>
            <div className="ok">Historique contrôlé — 2 propriétaires, CT valide, aucun gage</div>
            <div className="ok">Marché analysé — 14 comparables, tension normale</div>
            <div className="ok">3 points à contrôler relevés</div>
            <div className="lv2-demo-rows">
              <div className="lv2-demo-row"><span>Prix proposé</span><b>19 500 €</b></div>
              <div className="lv2-demo-row"><span>Charges estimées</span><b>1 100 €</b></div>
              <div className="lv2-demo-row"><span>Levier de négociation</span><b>650 €</b></div>
              <div className="lv2-demo-row total"><span>Marge potentielle</span><b>2 750 €</b></div>
            </div>
            <div className="lv2-demo-actions">
              <span className="lv2-btn secondary sm">Voir CarVertical</span>
              <span className="lv2-btn secondary sm">Comparer le marché</span>
              <span className="lv2-btn primary sm">Créer le dossier</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Mémoire, workflow, leads, réseau (§55-59) ───────────────────────────────
function VehicleMemory({ Illustration }) {
  return (
    <section className="lv2-section alt">
      <div className="lv2-wrap lv2-two">
        <div>
          <div className="lv2-eyebrow">Dossier VIQ</div>
          <h2 className="lv2-h2">Une mémoire pour chaque véhicule.</h2>
          <p className="lv2-lead">Analyses, photos, historiques, marché, estimations et actions : tout est centralisé dans un seul Dossier VIQ, du premier contact à la vente. Six mois plus tard, rien n'est à ressaisir.</p>
        </div>
        <div>{Illustration ? <Illustration /> : null}</div>
      </div>
    </section>
  );
}
function VOWorkflow({ onTry }) {
  const steps = ["Opportunité", "Contrôle", "Marché", "Négociation", "Achat", "Préparation", "Annonce", "Vente"];
  return (
    <section className="lv2-section">
      <div className="lv2-wrap lv2-center">
        <div className="lv2-eyebrow">Logiciel VO intelligent</div>
        <h2 className="lv2-h2">Pensé pour ceux qui achètent avant de revendre.</h2>
        <ol className="lv2-steps">{steps.map((s, i) => <li key={s} className="lv2-step"><i>{i + 1}</i>{s}</li>)}</ol>
        <button className="lv2-btn primary md" style={{ marginTop: "2rem" }} onClick={onTry}>Essayer VIQ.AI</button>
      </div>
    </section>
  );
}
function CRMSection() {
  return (
    <section className="lv2-section alt">
      <div className="lv2-wrap lv2-two">
        <div>
          <div className="lv2-eyebrow">CRM pour marchand automobile</div>
          <h2 className="lv2-h2">Du prospect au dossier, sans double saisie.</h2>
          <ul className="lv2-list">
            <li>Un formulaire client où la plaque suffit : le véhicule est identifié en arrière-plan.</li>
            <li>Le lead arrive enrichi — photos, HistoVec, identité décodée.</li>
            <li>Qualification, relances et agenda au même endroit.</li>
            <li>Le dossier se crée depuis le lead, avec tout ce qu'il contient.</li>
          </ul>
        </div>
        <div>
          <div className="lv2-eyebrow">Contrôle HistoVec et CarVertical</div>
          <h2 className="lv2-h2">Vérifiez avant d'acheter.</h2>
          <ul className="lv2-list">
            <li>CarVertical prérempli depuis la plaque ou le VIN — 20 % de réduction via VehicleIQ.</li>
            <li>HistoVec importé et lu : propriétaires, contrôles techniques, gage, sinistres déclarés.</li>
            <li>Points de vigilance chiffrés, prêts pour la négociation.</li>
          </ul>
        </div>
      </div>
    </section>
  );
}
function NetworkSection() {
  return (
    <section className="lv2-section">
      <div className="lv2-wrap lv2-center">
        <div className="lv2-eyebrow">Réseau VehicleIQ</div>
        <h2 className="lv2-h2">Les opportunités circulent aussi entre professionnels.</h2>
        <p className="lv2-lead">Publiez un dossier auprès du réseau, recevez des offres, attribuez le véhicule au professionnel de votre choix. Traçable de bout en bout, sans transmettre les coordonnées client avant accord.</p>
        <ol className="lv2-steps">{["Publier", "Recevoir des offres", "Attribuer", "Transférer"].map((s, i) => <li key={s} className="lv2-step"><i>{i + 1}</i>{s}</li>)}</ol>
      </div>
    </section>
  );
}

// ── Pricing (§60-61) ────────────────────────────────────────────────────────
function PricingV2({ plans, onTry, onQuote }) {
  return (
    <section className="lv2-section alt" id="pricing">
      <div className="lv2-wrap lv2-center">
        <div className="lv2-eyebrow">Tarification</div>
        <h2 className="lv2-h2">De l'intelligence automobile, à la demande.</h2>
        <div className="lv2-pricing">
          {plans.map(p => (
            <div key={p.id} className={`lv2-card lv2-plan${p.best ? " best" : ""}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div className="lv2-plan-name">{p.label}</div>
                {p.best && <span className="lv2-badge gold">Le plus choisi</span>}
              </div>
              <div className="lv2-plan-price">{p.price}</div>
              <div className="lv2-plan-per">{p.per}</div>
              <ul className="lv2-plan-list">{p.capabilities.slice(0, 6).map(c => <li key={c}>{c}</li>)}</ul>
              <button className={`lv2-btn ${p.best ? "primary" : "secondary"} md`} onClick={() => p.quote ? onQuote() : onTry(p.id)}>{p.cta}</button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Ressources (§76-78) ─────────────────────────────────────────────────────
function ResourcesPreview({ nav }) {
  const list = publishedArticles().slice(0, 3);
  const upcoming = ARTICLES.filter(a => a.status !== "published").slice(0, 3 - list.length);
  return (
    <section className="lv2-section" id="resources">
      <div className="lv2-wrap lv2-center">
        <div className="lv2-eyebrow">Ressources</div>
        <h2 className="lv2-h2">Comprendre le métier, pas seulement l'outil.</h2>
        <div className="lv2-articles">
          {list.map(a => (
            <button key={a.slug} className="lv2-card lv2-article" onClick={() => nav(`/blog/${a.slug}`)}>
              <div className="cat">{categoryLabel(a.category)}</div>
              <h3>{a.title}</h3><p>{a.excerpt}</p>
            </button>
          ))}
          {upcoming.map(a => (
            <div key={a.slug} className="lv2-card lv2-article" style={{ opacity: .6 }}>
              <div className="cat">{categoryLabel(a.category)} · à paraître</div>
              <h3>{a.title}</h3><p>{a.excerpt}</p>
            </div>
          ))}
        </div>
        <button className="lv2-btn secondary md" style={{ marginTop: "1.6rem" }} onClick={() => nav("/ressources")}>Toutes les ressources</button>
      </div>
    </section>
  );
}

// ── FAQ ─────────────────────────────────────────────────────────────────────
const FAQ = [
  ["VehicleIQ remplace-t-il mon logiciel de gestion ?", "VIQ.AI couvre l'analyse, le contrôle, le marché, la marge, les dossiers et le suivi client. Il s'utilise seul ou à côté d'un outil de gestion d'atelier ou de facturation existant."],
  ["Que se passe-t-il après les 7 jours gratuits ?", "Vous choisissez le forfait adapté à votre volume, ou vous arrêtez. Aucune carte bancaire n'est demandée pour l'essai, aucun engagement ensuite."],
  ["Mes données véhicules et clients sont-elles partagées ?", "Non. Les coordonnées client ne sont jamais transmises au réseau avant un accord explicite, et les moteurs IA n'entraînent pas leurs modèles sur vos données."],
  ["Puis-je utiliser VehicleIQ à plusieurs ?", "Oui. VIQ.AI Team inclut trois utilisateurs avec mémoire d'équipe et dossiers partagés ; VIQ.AI Enterprise couvre les réseaux multi-sites."],
  ["Faut-il un SIRET ?", "Pour un compte professionnel avec accès complet, oui. L'essai peut démarrer sans."],
];
function FAQV2() {
  const data = { "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) };
  return (
    <section className="lv2-section alt">
      <div className="lv2-wrap lv2-center">
        <h2 className="lv2-h2">Questions fréquentes</h2>
        <div className="lv2-faq">
          {FAQ.map(([q, a]) => <details key={q}><summary>{q}</summary><p>{a}</p></details>)}
        </div>
        <JsonLd data={data} />
      </div>
    </section>
  );
}

// ── CTA final, footer, WhatsApp (§66-67, §96) ───────────────────────────────
function FinalCTA({ onTry }) {
  return (
    <section className="lv2-section lv2-center">
      <div className="lv2-wrap">
        <h2 className="lv2-h2">Mettez VIQ.AI au travail sur vos véhicules.</h2>
        <button className="lv2-btn primary lg" style={{ marginTop: "1.6rem" }} onClick={onTry}>Essayer VIQ.AI</button>
        <div className="lv2-hero-cta-note" style={{ textAlign: "center" }}>7 jours gratuits · sans engagement</div>
      </div>
    </section>
  );
}
function PublicFooter({ nav, onLegal }) {
  const cols = [
    ["Produit", [["VIQ.AI", () => nav("viqai")], ["Tarification", () => nav("pricing")], ["API", () => nav("api")], ["Démonstration", () => nav("/demo")]]],
    ["Ressources", [["Blog", () => nav("/ressources")], ["FAQ", () => nav("faq")]]],
    ["Entreprise", [["À propos", () => nav("about")], ["Contact", () => nav("contact")]]],
    ["Légal", [["Mentions légales", () => onLegal("mentions")], ["Confidentialité", () => onLegal("privacy")], ["CGU", () => onLegal("cgu")], ["CGV", () => onLegal("cgv")]]],
  ];
  return (
    <footer className="lv2-footer">
      <div className="lv2-wrap">
        <div className="lv2-footer-grid">
          <div>
            <div className="lv2-brand" style={{ cursor: "default" }}>Vehicle<span>IQ</span></div>
            <p style={{ fontSize: ".84rem", color: "var(--viq-muted)", marginTop: ".6rem", maxWidth: 260 }}>Intelligence automobile professionnelle.</p>
          </div>
          {cols.map(([t, links]) => (
            <div key={t} className="lv2-footer-col"><h4>{t}</h4>{links.map(([l, fn]) => <button key={l} onClick={fn}>{l}</button>)}</div>
          ))}
        </div>
        <div className="lv2-footer-base">
          <span>© {new Date().getFullYear()} VehicleIQ — M.K.L Ingénierie et Conseil</span>
          <span>Conçu en France · Hébergé en Union européenne</span>
        </div>
      </div>
    </footer>
  );
}
export function WhatsAppButton({ number }) {
  const n = String(number || WA_NUMBER).replace(/[^\d]/g, "");
  if (!n) return null;
  return (
    <a className="lv2-wa" href={`https://wa.me/${n}?text=${encodeURIComponent(WA_TEXT)}`} target="_blank" rel="noopener noreferrer" aria-label="Nous écrire sur WhatsApp">
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5.1-1.3A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8s-.4-.1-.6.1-.6.8-.8 1-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.2-.4.7-1.3.1-.2 0-.3 0-.5l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7 11.8 11.8 0 0 0 4.5 4 5.2 5.2 0 0 0 3.1.6 2.6 2.6 0 0 0 1.7-1.2 2.1 2.1 0 0 0 .2-1.2c-.1-.1-.3-.2-.5-.3z"/></svg>
      <span>WhatsApp</span>
    </a>
  );
}

// ── Page /demo (§28-33) ─────────────────────────────────────────────────────
export function DemoPage({ apiUrl, onPrivacy, nav }) {
  return (
    <div className="lv2-demo-page">
      <div className="lv2-wrap lv2-demo-page-inner">
        <div className="lv2-eyebrow lv2-center">VehicleIQ</div>
        <h1>Découvrez VIQ.AI sur vos propres véhicules.</h1>
        <p className="lv2-lead">7 jours gratuits, sans engagement. Nous vous montrons comment VehicleIQ s'intègre à votre activité VO.</p>
        <DemoLeadForm apiUrl={apiUrl} onPrivacy={onPrivacy} onDiscover={() => nav("/")} variant="demo_page" />
      </div>
    </div>
  );
}

// ── Shell ───────────────────────────────────────────────────────────────────
export default function LandingV2({ apiUrl, route = "/", plans = [], providers = [], Illustration, onLogin, onTry, onQuote, onLegal, nav, whatsapp, articleContent = null }) {
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("viq_public_theme") || "light"; } catch (e) { return "light"; }
  });
  useEffect(() => { try { localStorage.setItem("viq_public_theme", theme); } catch (e) {} }, [theme]);

  // Navigation interne : ancres pour les sections, chemins pour les pages.
  const go = (target) => {
    if (target.startsWith("/")) return nav(target);
    const el = document.getElementById(target);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    else nav(target);
  };

  return (
    <div className="lv2" data-theme={theme}>
      <JsonLd data={ORG} /><JsonLd data={APP} />
      <PublicNavbar theme={theme} onTheme={() => setTheme(t => (t === "dark" ? "light" : "dark"))} nav={go} onLogin={onLogin} onTry={onTry} />
      {route === "/demo" ? (
        <DemoPage apiUrl={apiUrl} onPrivacy={onLegal} nav={go} />
      ) : route === "/ressources" ? (
        <ResourcesHub nav={go} />
      ) : route.startsWith("/blog/") ? (
        <ArticlePage slug={route.slice(6)} nav={go} onTry={onTry} content={articleContent} />
      ) : (
        <main>
          <HeroV2 apiUrl={apiUrl} onTry={onTry} onPrivacy={onLegal} nav={go} />
          <AIProviders live={providers} />
          <VIQCapabilities />
          <ConversationDemo />
          <VehicleMemory Illustration={Illustration} />
          <VOWorkflow onTry={onTry} />
          <CRMSection />
          <NetworkSection />
          <PricingV2 plans={plans} onTry={onTry} onQuote={onQuote} />
          <ResourcesPreview nav={go} />
          <FAQV2 />
          <FinalCTA onTry={onTry} />
        </main>
      )}
      <PublicFooter nav={go} onLegal={onLegal} />
      <WhatsAppButton number={whatsapp} />
    </div>
  );
}
